"use client";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

export interface SortableItemProps {
  id: string;
  children: React.ReactNode;
  className?: string;
  handleClassName?: string;
  disabled?: boolean;
  showDropIndicator?: boolean;
  cursorVariant?: "grab" | "pointer" | "default";
}

export function SortableItem({
  id,
  children,
  className,
  handleClassName,
  disabled = false,
  showDropIndicator: showDropIndicatorProp = false,
  cursorVariant,
}: SortableItemProps) {
  const isMobile = useIsMobile();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
    over,
  } = useSortable({
    id,
    disabled: isMobile || disabled,
  });

  // ドロップインジケーターの表示判定（propで制御）
  const showDropIndicator =
    showDropIndicatorProp && isOver && active && active.id !== id;

  // ドラッグ方向の判定（アイテムのインデックスで判定）
  const dragDirection = React.useMemo(() => {
    if (!active || !over || active.id === over.id) return null;

    const activeIndex = active.data.current?.sortable?.index;
    const overIndex = over.data.current?.sortable?.index;

    if (typeof activeIndex === "number" && typeof overIndex === "number") {
      return activeIndex > overIndex ? "up" : "down";
    }

    return null;
  }, [active, over]);

  const style = React.useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition],
  );

  const resolvedCursor = cursorVariant ?? (disabled ? "default" : "grab");
  const cursorClass =
    resolvedCursor === "pointer"
      ? "cursor-pointer active:cursor-pointer"
      : resolvedCursor === "default"
        ? "cursor-default"
        : "cursor-grab active:cursor-grabbing";

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-id={id}
      className={cn("relative", isDragging && "opacity-0", className)}
    >
      {/* ドロップインジケーター（上向きドラッグ時は上に、下向きドラッグ時は下に表示） */}
      {showDropIndicator && dragDirection === "up" && (
        <div
          className="absolute left-0 right-0 -top-0.5 h-0.5 bg-primary/80"
          style={{
            zIndex: 1000,
            pointerEvents: "none",
          }}
        />
      )}
      {showDropIndicator && dragDirection === "down" && (
        <div
          className="absolute left-0 right-0 -bottom-0.5 h-0.5 bg-primary/80"
          style={{
            zIndex: 1000,
            pointerEvents: "none",
          }}
        />
      )}
      <div
        {...attributes}
        {...listeners}
        className={cn(cursorClass, handleClassName)}
      >
        {children}
      </div>
    </div>
  );
}
